// Service User Profile — 12 sections config.
// Groups render in insertion order: Person → Care → Records.
// Section keys remain stable (backend routes depend on them); only display labels/order changed.
import {
  Info, HeartPulse, Users, BookOpen, AlertOctagon, CalendarDays,
  ScrollText, LineChart, FileText, ClipboardCheck, Pill, FileCheck2, Activity, Stethoscope, UtensilsCrossed,
} from "lucide-react";

export const CLIENT_SECTIONS = {
  // ============================ PERSON ============================
  "general": {
    key: "general",
    label: "Overview",
    icon: Info,
    color: "text-sky-600",
    group: "Person",
    viewType: "singleton",
    fields: [
      { name: "preferred_name", label: "Preferred Name", type: "text" },
      { name: "nhs_number", label: "NHS Number", type: "text" },
      { name: "gender", label: "Gender", type: "select", options: ["Female", "Male", "Non-binary", "Prefer not to say"] },
      { name: "ethnicity", label: "Ethnicity", type: "text" },
      { name: "religion", label: "Religion", type: "text" },
      { name: "language", label: "Primary Language", type: "text" },
      { name: "marital_status", label: "Marital Status", type: "select", options: ["Single", "Married", "Civil Partnership", "Divorced", "Widowed"] },
      { name: "address_line", label: "Address", type: "textarea" },
      { name: "postcode", label: "Postcode", type: "text" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  "about-me": {
    key: "about-me",
    label: "About Me",
    icon: BookOpen,
    color: "text-violet-600",
    group: "Person",
    viewType: "singleton",
    fields: [
      { name: "background", label: "Life History / Background", type: "textarea" },
      { name: "likes", label: "Likes", type: "textarea" },
      { name: "dislikes", label: "Dislikes", type: "textarea" },
      { name: "communication", label: "How I communicate", type: "textarea" },
      { name: "routine", label: "My routine", type: "textarea" },
      { name: "hopes", label: "My hopes & aspirations", type: "textarea" },
    ],
  },
  "needs": {
    key: "needs",
    label: "My Needs",
    icon: HeartPulse,
    color: "text-rose-600",
    group: "Person",
    viewType: "list",
    fields: [
      { name: "category", label: "Category", type: "select", options: ["Physical", "Mental Health", "Communication", "Mobility", "Sensory", "Social", "Behavioural", "Cognitive", "Other"], required: true },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "level", label: "Support Level", type: "select", options: ["Minimal", "Moderate", "Significant", "Complex"], required: true },
      { name: "notes", label: "Notes / How to support", type: "textarea" },
    ],
    columns: [
      { key: "category", label: "Category" },
      { key: "description", label: "Description" },
      { key: "level", label: "Level", status: "severity" },
    ],
  },
  "important-people": {
    key: "important-people",
    label: "Important People",
    icon: Users,
    color: "text-emerald-600",
    group: "Person",
    viewType: "list",
    fields: [
      { name: "role", label: "Role", type: "select", options: ["GP", "Family", "Friend", "Professional", "Next of Kin", "Advocate", "Solicitor", "Emergency Contact"], required: true },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "relationship", label: "Relationship / Title", type: "text" },
      { name: "organization", label: "Organisation", type: "text" },
      { name: "phone", label: "Phone", type: "text" },
      { name: "email", label: "Email", type: "email" },
      { name: "address", label: "Address", type: "textarea" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "role", label: "Role" },
      { key: "name", label: "Name" },
      { key: "relationship", label: "Relationship" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
    ],
  },
  "future-plans": {
    key: "future-plans",
    label: "Future Goals",
    icon: CalendarDays,
    color: "text-amber-600",
    group: "Person",
    viewType: "list",
    fields: [
      { name: "goal", label: "Goal / Plan", type: "text", required: true },
      { name: "target_date", label: "Target Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["planned", "in_progress", "achieved", "on_hold"], required: true },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "goal", label: "Goal" },
      { key: "target_date", label: "Target" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  "alerts": {
    key: "alerts",
    label: "Risk Assessments",
    icon: AlertOctagon,
    color: "text-red-600",
    group: "Care",
    viewType: "list",
    fields: [
      { name: "assessment_type", label: "Assessment Type", type: "select", options: ["Manual Handling", "Falls Risk", "Choking Risk", "Medication", "Behaviour / PBS", "Self-neglect", "Financial Abuse", "Fire / Evacuation", "Bed Rails", "Skin Integrity", "Nutrition / Hydration", "Environmental", "Other"], required: true },
      { name: "title", label: "Title", type: "text", required: true, placeholder: "e.g. Falls risk on stairs" },
      { name: "severity", label: "Severity", type: "select", options: ["low", "medium", "high", "critical"], required: true },
      { name: "likelihood", label: "Likelihood", type: "select", options: ["rare", "unlikely", "possible", "likely", "almost certain"] },
      { name: "impact", label: "Impact if realised", type: "select", options: ["negligible", "minor", "moderate", "major", "catastrophic"] },
      { name: "description", label: "Hazard / risk described", type: "textarea" },
      { name: "controls", label: "Controls in place", type: "textarea", placeholder: "e.g. hand-rails, 2-person transfer, motion sensor at night" },
      { name: "residual_risk", label: "Residual risk after controls", type: "select", options: ["low", "medium", "high", "critical"] },
      { name: "reviewed_by", label: "Assessed by", type: "text" },
      { name: "review_date", label: "Reviewed on", type: "date" },
      { name: "next_review_date", label: "Next review date", type: "date" },
      { name: "active", label: "Active", type: "select", options: ["yes", "no"], required: true },
    ],
    columns: [
      { key: "severity", label: "Severity", status: "severity" },
      { key: "assessment_type", label: "Type" },
      { key: "title", label: "Title" },
      { key: "residual_risk", label: "Residual", status: "severity" },
      { key: "next_review_date", label: "Next Review", status: "expiry" },
      { key: "active", label: "Active" },
    ],
  },

  // ============================ CARE ============================
  "care-planning": {
    key: "care-planning",
    label: "Care Plan",
    icon: ClipboardCheck,
    color: "text-indigo-600",
    group: "Care",
    viewType: "list",
    showRecordMeta: true, // renders v{version} · Created · Last updated header in editor
    fields: [
      { name: "plan_type", label: "Care Plan Type", type: "grouped_select", groupsFrom: "carePlanTypes", required: true, sideEffect: "carePlanTypeSideEffect" },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "priority", label: "Priority", type: "select", options: ["High", "Medium", "Low"] },
      { name: "progress", label: "Progress", type: "select", options: ["On Track", "Improving", "Needs Review", "Goal Achieved"] },
      { name: "goals", label: "Person-centred Goals", type: "richtext", placeholder: "e.g. Support John to shop independently by Q4" },
      { name: "desired_outcomes", label: "Desired Outcomes", type: "richtext", placeholder: "What good looks like for this person — measurable outcomes and success criteria" },
      { name: "interventions", label: "Interventions / Support Required", type: "richtext", placeholder: "e.g. 1:1 support 3 hrs/week, visual timetable" },
      { name: "person_voice", label: "What Matters to Me", type: "richtext", placeholder: "What the service user has said about this plan, in their own words" },
      { name: "summary", label: "Professional Guidance", type: "richtext", placeholder: "Clinical & practitioner guidance for the team" },
      { name: "reviewed_by", label: "Reviewed By", type: "text" },
      { name: "reviewed_date", label: "Reviewed Date", type: "date" },
      { name: "next_review_date", label: "Next Review Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["draft", "active", "under_review", "archived"] },
    ],
    columns: [
      { key: "plan_type", label: "Type" },
      { key: "title", label: "Title" },
      { key: "priority", label: "Priority", render: "priority" },
      { key: "progress", label: "Progress", render: "progress" },
      { key: "next_review_date", label: "Review Status", render: "review_status" },
      { key: "version", label: "Version", render: "version" },
      { key: "next_review_date", label: "Next Review", key2: "_date" }, // date-only column
    ],
  },
  "medication": {
    key: "medication",
    label: "Medication",
    icon: Pill,
    color: "text-red-600",
    group: "Care",
    viewType: "list",
    fields: [
      { name: "medication_name", label: "Medication", type: "text", required: true },
      { name: "dose", label: "Dose", type: "text", required: true },
      { name: "route", label: "Route", type: "select", options: ["Oral", "Topical", "Sublingual", "Inhaled", "Injected", "Rectal", "Other"] },
      { name: "frequency", label: "Frequency", type: "select", options: ["Once daily", "Twice daily", "3x daily", "4x daily", "PRN (as needed)", "Weekly", "Custom"] },
      { name: "schedule_times", label: "Scheduled times (HH:MM, comma-separated)", type: "text", placeholder: "08:00, 12:00, 20:00" },
      { name: "is_controlled_drug", label: "Controlled Drug (CD)?", type: "select", options: ["no", "yes"] },
      { name: "min_interval_hours", label: "Min hours between PRN doses", type: "number", placeholder: "e.g. 4" },
      { name: "stock", label: "Current Stock", type: "number" },
      { name: "prescriber", label: "Prescriber", type: "text" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "end_date", label: "End Date", type: "date" },
      { name: "status", label: "Status", type: "select", options: ["active", "discontinued", "on_hold"], required: true },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "medication_name", label: "Medication" },
      { key: "dose", label: "Dose" },
      { key: "frequency", label: "Frequency" },
      { key: "stock", label: "Stock", numeric: true },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  "allergies-diet": {
    key: "allergies-diet",
    label: "Allergies & Diet",
    icon: UtensilsCrossed,
    color: "text-orange-700",
    group: "Care",
    viewType: "list",
    fields: [
      { name: "kind", label: "Kind", type: "select", options: ["Allergy", "Intolerance", "Dietary preference", "Texture / consistency", "Fluid consistency", "Cultural / religious", "Other"], required: true },
      { name: "item", label: "Item / label", type: "text", required: true, placeholder: "e.g. Peanuts, Vegan, IDDSI Level 5 minced" },
      { name: "severity", label: "Severity", type: "select", options: ["mild", "moderate", "severe", "life-threatening", "n/a"] },
      { name: "reaction", label: "Typical reaction", type: "textarea", placeholder: "e.g. rash within 20 min, anaphylaxis" },
      { name: "management", label: "Management / avoidance plan", type: "textarea", placeholder: "e.g. EpiPen kept in medication cupboard, avoid all nut oils" },
      { name: "confirmed_by", label: "Confirmed by", type: "text", placeholder: "e.g. GP, Dietitian" },
      { name: "date_recorded", label: "Date confirmed", type: "date" },
      { name: "active", label: "Active", type: "select", options: ["yes", "no"] },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "kind", label: "Kind" },
      { key: "item", label: "Item" },
      { key: "severity", label: "Severity", status: "severity" },
      { key: "reaction", label: "Reaction" },
      { key: "confirmed_by", label: "Confirmed by" },
      { key: "active", label: "Active" },
    ],
  },
  "appointments": {
    key: "appointments",
    label: "Appointments",
    icon: Stethoscope,
    color: "text-indigo-700",
    group: "Care",
    viewType: "list",
    fields: [
      { name: "title", label: "Title", type: "text", required: true, placeholder: "e.g. GP review, Neurology follow-up" },
      { name: "appointment_type", label: "Type", type: "select", options: ["GP", "Consultant", "Dentist", "Optician", "Physio", "Review", "Hospital", "Telephone", "Other"], required: true },
      { name: "scheduled_at", label: "Date & time", type: "datetime-local", required: true },
      { name: "duration_min", label: "Duration (min)", type: "number", placeholder: "e.g. 30" },
      { name: "location", label: "Location", type: "text", placeholder: "e.g. Bridge Surgery" },
      { name: "clinician", label: "Clinician / Contact", type: "text" },
      { name: "escort_staff", label: "Escort / Attending staff", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["scheduled", "completed", "cancelled", "no_show"], required: true },
      { name: "notes", label: "Notes / reason", type: "textarea" },
    ],
    columns: [
      { key: "scheduled_at", label: "When" },
      { key: "appointment_type", label: "Type" },
      { key: "title", label: "Title" },
      { key: "location", label: "Location" },
      { key: "clinician", label: "Clinician" },
      { key: "status", label: "Status", status: "generic" },
    ],
  },
  "vitals": {
    key: "vitals",
    label: "Vitals & Health",
    icon: Activity,
    color: "text-rose-700",
    group: "Care",
    viewType: "list",
    fields: [
      { name: "measured_at", label: "When", type: "datetime-local", required: true },
      { name: "systolic_bp", label: "Systolic BP (mmHg)", type: "number", placeholder: "e.g. 120" },
      { name: "diastolic_bp", label: "Diastolic BP (mmHg)", type: "number", placeholder: "e.g. 80" },
      { name: "pulse", label: "Pulse (bpm)", type: "number", placeholder: "60-100" },
      { name: "temperature_c", label: "Temperature (C)", type: "number", placeholder: "36.1-37.2" },
      { name: "spo2", label: "SpO2 (%)", type: "number", placeholder: "95 or above" },
      { name: "respiration_rate", label: "Respiration rate (breaths/min)", type: "number", placeholder: "12-20" },
      { name: "blood_glucose", label: "Blood glucose (mmol/L)", type: "number", placeholder: "4.0-7.8" },
      { name: "weight_kg", label: "Weight (kg)", type: "number" },
      { name: "pain_score", label: "Pain score (0-10)", type: "number" },
      { name: "consciousness", label: "Consciousness (AVPU)", type: "select", options: ["Alert", "Voice", "Pain", "Unresponsive"] },
      { name: "notes", label: "Notes / context", type: "textarea" },
    ],
    columns: [
      { key: "measured_at", label: "When" },
      { key: "bp_display", label: "BP (mmHg)" },
      { key: "pulse", label: "Pulse", numeric: true },
      { key: "temperature_c", label: "Temp C", numeric: true },
      { key: "spo2", label: "SpO2 %", numeric: true },
      { key: "respiration_rate", label: "RR", numeric: true },
      { key: "flag", label: "Status", status: "vitals" },
    ],
  },
  "consent": {
    key: "consent",
    label: "Consent",
    icon: FileCheck2,
    color: "text-teal-600",
    group: "Care",
    viewType: "list",
    fields: [
      { name: "topic", label: "Consent Topic", type: "text", required: true },
      { name: "granted", label: "Granted?", type: "select", options: ["Yes", "No", "Best Interest Decision"], required: true },
      { name: "granted_by", label: "Granted By", type: "text" },
      { name: "granted_date", label: "Granted Date", type: "date", required: true },
      { name: "review_date", label: "Review Date", type: "date" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "topic", label: "Topic" },
      { key: "granted", label: "Granted" },
      { key: "granted_by", label: "Granted By" },
      { key: "granted_date", label: "Date" },
      { key: "review_date", label: "Review", status: "expiry" },
    ],
  },

  // ============================ RECORDS ============================
  "daily-logs": {
    key: "daily-logs",
    label: "Daily Notes",
    icon: ScrollText,
    color: "text-slate-600",
    group: "Records",
    viewType: "timeline",
    fields: [
      { name: "occurred_at", label: "When", type: "datetime-local", required: true },
      { name: "activity", label: "Activity", type: "select", options: ["Water Refill", "Getting Dressed", "Getting Washed", "Oral Care", "Meal", "Medication", "Mood", "Toileting", "Sleep", "Activity", "Behaviour", "Note", "Other"], required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "notes", label: "Notes", type: "textarea", required: true },
    ],
  },
  "charts": {
    key: "charts",
    label: "Observations",
    icon: LineChart,
    color: "text-sky-700",
    group: "Records",
    viewType: "list",
    fields: [
      { name: "measured_at", label: "Date & Time", type: "datetime-local", required: true },
      { name: "metric", label: "Metric", type: "select", options: ["Weight", "Blood Pressure", "Temperature", "Heart Rate", "Blood Sugar", "Oxygen Saturation", "Mood", "Pain Score", "Fluid Intake", "Food Intake", "Other"], required: true },
      { name: "value", label: "Value", type: "text", required: true },
      { name: "unit", label: "Unit", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
    columns: [
      { key: "measured_at", label: "When" },
      { key: "metric", label: "Metric" },
      { key: "value", label: "Value", numeric: true },
      { key: "unit", label: "Unit" },
    ],
  },
  "documentation": {
    key: "documentation",
    label: "Documents",
    icon: FileText,
    color: "text-amber-700",
    group: "Records",
    viewType: "list",
    fields: [
      { name: "doc_type", label: "Document Type", type: "select", options: ["Care Plan", "Assessment", "Consent", "Medical", "Correspondence", "Photo", "Financial", "Other"], required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
    ],
    columns: [
      { key: "doc_type", label: "Type" },
      { key: "title", label: "Title" },
      { key: "description", label: "Description" },
    ],
  },
};

export const CLIENT_SECTION_LIST = Object.values(CLIENT_SECTIONS);
